import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'ui/Modal';
import AlignmentForm from 'components/Common/AlignmentForm';
import { updateAboutSetting, PrivacyLevel } from 'util/userUtils';
import { toast } from 'sonner';
import { useUser } from 'util/UserContext';
import api from 'config/axiosConfig';
import { Loader2 } from 'lucide-react';
import { apiRoutes } from "util/apiRoutes";

interface UserAlignmentModalProps {
    onClose: () => void;
    initialStatementId?: number | null;
}

interface AlignmentAnswer {
    id: number;
    response: number;
    privacy: PrivacyLevel;
    note: string;
    importance: number;
    created_at: string;
    updated_at: string;
}

interface AlignmentStatement {
    id: number;
    statement_id: number;
    statement_text: string;
    is_active: boolean;
    properties: Record<string, any>;
    created_at: string;
    updated_at: string;
}

const UserAlignmentModal: React.FC<UserAlignmentModalProps> = ({ onClose, initialStatementId }) => {
    const { currentUser } = useUser();
    const [currentStatement, setCurrentStatement] = useState(0);
    const [answers, setAnswers] = useState<AlignmentAnswer[]>([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const isUserPrivate = currentUser?.properties_public?.is_private === true;
    const [apiStatements, setApiStatements] = useState<AlignmentStatement[]>([]);
    const [isLoadingStatements, setIsLoadingStatements] = useState<boolean>(true);
    const [statementsError, setStatementsError] = useState<string | null>(null);
    const [responseHasChanged, setResponseHasChanged] = useState<boolean | null>(null);

    const statements = useMemo(() => {
        if (apiStatements.length === 0) {
            return [];
        }
        
        return apiStatements
            .sort((a, b) => a.statement_id - b.statement_id)
            .map(statement => ({ 
                id: statement.statement_id,
                statement: statement.statement_text 
            }));
    }, [apiStatements]);

    useEffect(() => {
        const fetchAlignmentStatements = async () => {
            try {
                setIsLoadingStatements(true);
                const response = await api.get(apiRoutes.alignment.statements);
                if (response.data && response.data.status === 'success') {
                    setApiStatements(response.data.data);
                } else {
                    setStatementsError('Failed to load alignment statements');
                }
            } catch (error) {
                console.error('Error fetching alignment statements:', error);
                setStatementsError('Failed to load alignment statements');
            } finally {
                setIsLoadingStatements(false);
            }
        };

        fetchAlignmentStatements();
    }, []);

    // Load existing answers from user settings
    useEffect(() => {
        const fetchUserAnswers = async () => {
            if (!currentUser || statements.length === 0) return;
            
            try {
                const initialAnswers: AlignmentAnswer[] = [];
                
                // Iterate through possible answer IDs
                for (let i = 0; i < statements.length; i++) {
                    const answerId = statements[i].id;
                    const answerKey = `about_alignment_answer_${answerId}`;
                    const savedAnswer = currentUser.properties?.[answerKey];
                    
                    if (savedAnswer) {
                        try {
                            // If we have saved data, parse it
                            const parsedAnswer = JSON.parse(savedAnswer.value || '{}');
                            const privacy = savedAnswer.privacy as PrivacyLevel || (isUserPrivate ? 'follow' : 'public');
                            
                            initialAnswers[i] = {
                                id: answerId,
                                response: parsedAnswer.response || 0,
                                importance: parsedAnswer.importance || 0,
                                note: parsedAnswer.note || '',
                                privacy,
                                created_at: parsedAnswer.created_at || new Date().toISOString(),
                                updated_at: new Date().toISOString()
                            };
                        } catch (error) {
                            console.error(`Error parsing saved answer ${answerId}:`, error);
                            initialAnswers[i] = createDefaultAnswer(answerId);
                        }
                    } else {
                        // If no saved data, create default
                        initialAnswers[i] = createDefaultAnswer(answerId);
                    }
                }
                
                setAnswers(initialAnswers);
                setInitialized(true);
            } catch (error) {
                console.error('Error loading perspective answers:', error);
                // Initialize with defaults if loading fails
                initializeDefaultAnswers();
            }
        };

        if (statements.length > 0) {
            fetchUserAnswers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, statements]);

    const createDefaultAnswer = (id: number): AlignmentAnswer => ({
        id,
        response: null as any,
        importance: 0,
        note: '',
        privacy: isUserPrivate ? 'follow' : 'public',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
    });

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await saveCurrentAnswer();
            setResponseHasChanged(false);
            onClose();
        } catch (error) {
            console.error('Error updating perspective:', error);
            toast.error('Failed to update perspective');
        } finally {
            setLoading(false);
        }
    };

    const handlePanelOpenChange = (open: boolean) => {
        setIsPanelOpen(open);
    };

    const handleResponseChange = (value: number) => {
        const newAnswers = [...answers];
        if (newAnswers[currentStatement]) {
            newAnswers[currentStatement] = {
                ...newAnswers[currentStatement],
                response: value,
                updated_at: new Date().toISOString()
            };
            setAnswers(newAnswers);
            setResponseHasChanged(true);
        }
    };

    const handleImportanceChange = (value: number) => {
        if (!answers[currentStatement] || !currentUser) return;
        
        // Get the current answer
        const answer = answers[currentStatement];
        
        // If response is null (not selected), don't save
        if (answer.response !== null) {
            setResponseHasChanged(true);
        }
        const newAnswers = [...answers];
        if (newAnswers[currentStatement]) {
            newAnswers[currentStatement] = {
                ...newAnswers[currentStatement],
                importance: value,
                updated_at: new Date().toISOString()
            };
            setAnswers(newAnswers);
        }
    };

    const handleNoteChange = (value: string) => {
        if (!answers[currentStatement] || !currentUser) return;
        
        // Get the current answer
        const answer = answers[currentStatement];
        
        // If response is null (not selected), don't save
        if (answer.response !== null) {
            setResponseHasChanged(true);
        }

        const newAnswers = [...answers];
        if (newAnswers[currentStatement]) {
            newAnswers[currentStatement] = {
                ...newAnswers[currentStatement],
                note: value,
                updated_at: new Date().toISOString()
            };
            setAnswers(newAnswers);
        }
    };

    const handlePrivacyChange = (value: PrivacyLevel) => {
        if (!answers[currentStatement] || !currentUser) return;
        
        // Get the current answer
        const answer = answers[currentStatement];
        
        // If response is null (not selected), don't save
        if (answer.response !== null) {
            setResponseHasChanged(true);
        }

        const newAnswers = [...answers];
        if (newAnswers[currentStatement]) {
            newAnswers[currentStatement] = {
                ...newAnswers[currentStatement],
                privacy: value,
                updated_at: new Date().toISOString()
            };
            setAnswers(newAnswers);
        }
    };

    /*const checkResponseChange = async () => {
        
        if (!answers[currentStatement] || !currentUser) return;
        
        // Get the current answer
        const answer = answers[currentStatement];
        
        // Get the current persisted answer from user properties if it exists
        const answerKey = `about_alignment_answer_${answer.id}`;
        const savedAnswer = currentUser.properties?.[answerKey];
        
        // If response is null (not selected), don't save
        if (answer.response === null) {
            setResponseHasChanged(false);
            return;
        }

        if (!savedAnswer) {
            setResponseHasChanged(true);
        } 

        // If the response has changed, set the state
        if (answer.response !== savedAnswer?.response) {
            setResponseHasChanged(true);
        }

        else if (savedAnswer) {
            try {
                const parsedAnswer = JSON.parse(savedAnswer.value || '{}');
                
                // Check if any of the key fields have changed
                if (parsedAnswer.response !== answer.response || 
                    parsedAnswer.importance !== answer.importance ||
                    parsedAnswer.note !== answer.note ||
                    savedAnswer.privacy !== answer.privacy) {
                    setResponseHasChanged(true);
                }
            } catch (error) {
                console.error('Error parsing saved answer:', error);
                setResponseHasChanged(true); // Save if we can't parse the previous answer
            }
        }
    };*/

    const saveCurrentAnswer = async () => {
        if (!answers[currentStatement] || !currentUser) return;
        
        // Get the current answer
        const answer = answers[currentStatement];
        
        // Get the current persisted answer from user properties if it exists
        const answerKey = `about_alignment_answer_${answer.id}`;
        const savedAnswer = currentUser.properties?.[answerKey];
        let hasChanged = false;
        
        // If response is null (not selected), don't save
        if (answer.response === null) {
            return;
        }
        
        // If no saved answer exists and the user has selected a response, save it
        if (!savedAnswer) {
            hasChanged = true;
        } 
        // If saved answer exists, check if any fields have changed
        else if (savedAnswer) {
            try {
                const parsedAnswer = JSON.parse(savedAnswer.value || '{}');
                
                // Check if any of the key fields have changed
                if (parsedAnswer.response !== answer.response || 
                    parsedAnswer.importance !== answer.importance ||
                    parsedAnswer.note !== answer.note ||
                    savedAnswer.privacy !== answer.privacy) {
                    hasChanged = true;
                }
            } catch (error) {
                console.error('Error parsing saved answer:', error);
                hasChanged = true; // Save if we can't parse the previous answer
            }
        }
        
        // Only proceed if there are changes to save
        if (!hasChanged) {
            return;
        }
        
        setUpdating(true);
        try {
            // Prepare answer data for saving
            const answerData = {
                statement_id: answer.id,
                response: answer.response,
                importance: answer.importance,
                note: answer.note,
                created_at: answer.created_at,
                updated_at: new Date().toISOString()
            };
            
            // Save answer with appropriate privacy level
            await updateAboutSetting('alignment', `answer_${answer.id}`, JSON.stringify(answerData), answer.privacy);
            if (currentUser?.properties) {
                currentUser.properties[`about_alignment_answer_${answer.id}`] = {
                    value: JSON.stringify(answerData),
                    privacy: answer.privacy
                };
            }
            
            toast.success('Answer updated successfully');
        } catch (error) {
            console.error('Error updating answer:', error);
            toast.error('Failed to update answer');
        } finally {
            setResponseHasChanged(false);
            setUpdating(false);
        }
    };

    // Update the findNextUnansweredIndex function with better error handling
    const findNextUnansweredIndex = (currentIndex: number, includeCurrentIndex = false) => {
        // Safety check - if answers are not ready yet, return 0
        if (!answers || answers.length === 0) {
            return 0;
        }
        
        // Start from current index + 1 (or current index if includeCurrentIndex is true)
        const startIndex = includeCurrentIndex ? currentIndex : currentIndex + 1;
        
        // First check for unanswered statements after the current index
        for (let i = startIndex; i < Math.min(answers.length, statements.length); i++) {
            // Make sure this answer exists before checking response
            if (answers[i] && answers[i].response === null) {
                return i;
            }
        }
        
        // If not found and we're not at the start, check from the beginning
        if (currentIndex > 0) {
            for (let i = 0; i < currentIndex; i++) {
                // Make sure this answer exists before checking response
                if (answers[i] && answers[i].response === null) {
                    return i;
                }
            }
        }
        
        // If still not found, return the next index or wrap around
        return (currentIndex + 1) % Math.min(answers.length, statements.length);
    };

    // Also update the initialization effect to have better guards
    useEffect(() => {
        if (initialized && statements.length > 0 && answers.length > 0) {
            try {
                // Only run once after initialization
                const firstUnansweredIndex = findNextUnansweredIndex(-1, true);
                setCurrentStatement(firstUnansweredIndex);
            } catch (error) {
                console.error("Error finding first unanswered statement:", error);
                // Fallback to the first statement if there's an error
                setCurrentStatement(0);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized]);

    // Update handleNext to navigate to next unanswered statement
    const handleNext = async () => {
        // Save current answer before moving to next
        await saveCurrentAnswer();
        
        // Find the next unanswered statement
        const nextIndex = findNextUnansweredIndex(currentStatement);
        setCurrentStatement(nextIndex);
    };

    // Add a handlePrevious function for the previous button
    const handlePrevious = async () => {
        // Save current answer before moving to previous
        await saveCurrentAnswer();
        
        // Move to previous statement
        const prevIndex = (currentStatement - 1 + statements.length) % statements.length;
        setCurrentStatement(prevIndex);
    };

    const initializeDefaultAnswers = () => {
        const defaultAnswers: AlignmentAnswer[] = [];
        
        for (let i = 0; i < statements.length; i++) {
            defaultAnswers[i] = createDefaultAnswer(statements[i].id);
        }
        
        setAnswers(defaultAnswers);
        setInitialized(true);
    };

    // Add effect for initialStatementId after the answers are loaded
    useEffect(() => {
        if (initialStatementId && statements.length > 0 && answers.length > 0 && initialized) {
            // Find the index of the statement with the specified ID
            const statementIndex = statements.findIndex(s => s.id === initialStatementId);
            
            if (statementIndex !== -1) {
                setCurrentStatement(statementIndex);
            } else {
            }
        }
    }, [initialStatementId, statements, answers, initialized]);

    if (isLoadingStatements) {
        return (
            <Modal
                isOpen={true}
                onClose={onClose}
                title="Share your perspective"
            >
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="size-4 animate-spin" />
                </div>
            </Modal>
        );
    }

    if (statementsError || statements.length === 0) {
        return (
            <Modal
                isOpen={true}
                onClose={onClose}
                title="Error"
            >
                <div className="text-center p-4">
                    <p className="text-red-500">{statementsError || 'No alignment statements available'}</p>
                    <button 
                        onClick={onClose}
                        className="mt-4 btn-primary"
                    >
                        Close
                    </button>
                </div>
            </Modal>
        );
    }

    if (!initialized) {
        return null;
    }

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title="Share your perspective"
        >
        <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto">
            <div className="flex flex-col flex-grow space-y-4">
                <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    For each statement, select your level of agreement. You can also modify the privacy of your responses, highlight the topic's importance, and set a note on your stance.
                </p>
                
                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto min-h-0 h-full">
                    <AlignmentForm
                        statement={statements[currentStatement].statement}
                        statements={statements.map(s => s.statement)}
                        currentStatementIndex={currentStatement}
                        response={answers[currentStatement].response}
                        importance={answers[currentStatement].importance}
                        note={answers[currentStatement].note}
                        privacy={answers[currentStatement].privacy}
                        onResponseChange={handleResponseChange}
                        onImportanceChange={handleImportanceChange}
                        onNoteChange={handleNoteChange}
                        onPrivacyChange={handlePrivacyChange}
                        onPanelOpenChange={handlePanelOpenChange}
                        onStatementChange={async (index) => {
                            if (index !== currentStatement) {
                                await saveCurrentAnswer();
                                setCurrentStatement(index);
                            }
                        }}
                        isPanelOpen={isPanelOpen}
                        actorType="user"
                        viewerRelation={'self'}
                        isActorPrivate={isUserPrivate}
                        allAnswers={answers}
                    />
                </div>
                
                {/* Sticky footer with buttons */}
                <div className="flex justify-between py-4 bg-eoe-bg dark:bg-eoe-dark-bg-modal border-t border-eoe-border-main dark:border-eoe-dark-border-main sticky bottom-4">
                    <button
                        className="btn-secondary"
                        onClick={handlePrevious}
                    >
                        Previous
                    </button>
                    {responseHasChanged && !initialStatementId &&
                    <button
                        onClick={saveCurrentAnswer}
                        disabled={updating}
                        className={`px-3 ${responseHasChanged && !updating ? 'btn-primary' : 'btn-secondary-disabled'}`}
                    >
                            {updating ? <Loader2 className="size-4 animate-spin" /> : 'Update'}
                        </button>
                    }

                    {currentStatement !== statements.length - 1 && responseHasChanged && !initialStatementId &&
                    <button
                        onClick={() => setCurrentStatement(prev => Math.min(prev + 1, statements.length - 1))}
                        disabled={currentStatement === statements.length - 1 || updating}
                        className="btn-secondary px-3"
                    >
                        Skip
                    </button>
                    }
                    
                    {currentStatement < statements.length - 1 && !initialStatementId ? (
                        <button
                            onClick={handleNext}
                            disabled={updating}
                            className={`btn-primary px-3 ${responseHasChanged ? 'btn-primary' : 'btn-secondary'}`}
                        >
                            {updating ? <Loader2 className="size-4 animate-spin" /> : responseHasChanged ? 'Next' : 'Skip'}
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={loading || updating}
                            className="btn-primary px-3"
                        >
                            {loading ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
                        </button>
                    )}
                </div>
            </div>
        </div>
        </Modal>
    );
};

export default UserAlignmentModal;
